import { isPathAtOrBelow, usePrompt } from "@redotech/react-router-util/prompt";
import { genericMemo } from "@redotech/react-util/component";
import { Input } from "@redotech/ui/form";
import { createContext, FormEventHandler, ReactNode, useContext } from "react";
import * as formCss from "./form.module.css";
import { UnsavedChangesGuard } from "./unsaved-changes-guard";

export const FormNavigationModalContext = createContext(false);

function NativeNavigationPrompt({
  isDirty,
  blockQueryChanges,
  samePagePathPrefix,
}: {
  isDirty: boolean;
  blockQueryChanges: boolean;
  samePagePathPrefix?: string;
}) {
  usePrompt(
    isDirty
      ? "You have unsaved changes. Would you like to discard these changes?"
      : null,
    blockQueryChanges,
    samePagePathPrefix,
  );
  return null;
}

export const Form = genericMemo(function Form<T>({
  children,
  id,
  input,
  initial,
  onSubmit,
  onReset,
  blockQueryChanges = true,
  samePagePathPrefix,
}: {
  id: string;
  initial: T;
  input: Input<T>;
  onSubmit?(): void;
  onReset?(): void;
  blockQueryChanges?: boolean;
  samePagePathPrefix?: string;
  children: ReactNode | ReactNode[];
}) {
  const useNavigationModal = useContext(FormNavigationModalContext);

  const handleReset: FormEventHandler = (e) => {
    e.preventDefault();
    input.setValue(initial);
    onReset?.();
  };

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    onSubmit?.();
  };

  return (
    <form
      className={formCss.form}
      data-web="Form"
      id={id}
      onReset={handleReset}
      onSubmit={handleSubmit}
    >
      {useNavigationModal ? (
        <UnsavedChangesGuard
          allowNavigation={({ currentLocation, nextLocation }) =>
            samePagePathPrefix !== undefined &&
            isPathAtOrBelow(currentLocation.pathname, samePagePathPrefix) &&
            isPathAtOrBelow(nextLocation.pathname, samePagePathPrefix)
          }
          blockQueryChanges={blockQueryChanges}
          isDirty={input.changed}
        />
      ) : (
        <NativeNavigationPrompt
          blockQueryChanges={blockQueryChanges}
          isDirty={input.changed}
          samePagePathPrefix={samePagePathPrefix}
        />
      )}
      {children}
    </form>
  );
});
